import Arrow from '@c/icons/buttons/ArrowButton';
import { useEffect, useRef, useState, useCallback, memo } from 'react';
import useIntersectionObserver from '@util/hooks/useIntersectionObserver';

import SafeImage from '@ui/SafeImage';
import Link from 'next/link';

import { MasterDocument } from '@util/firestore/master';

interface AffiliateProps {
  affiliates: string[];
  placement: 'start' | 'end';
}

interface CarouselProps {
  items: React.ReactNode[];
  affiliate?: AffiliateProps;
  master?: MasterDocument;
  incrementBy?: number;
  showPaginator?: boolean;
}

// Memoize AffiliateCard to prevent unnecessary renders
const AffiliateCard = memo(({ affiliate, master }: { affiliate: string; master: MasterDocument }) => {
  return (
    <div className="flex">
      <div 
        className="
          flex flex-col items-center justify-center p-[2rem]
          h-full w-[14.4rem] sm:w-[21.2rem] rounded-[1rem]
          bg-brand-white border-2 border-brand-secondary text-brand-secondary" 
        key={`${affiliate}-affiliate-link`}
      >
        <div className="flex items-center h-[15rem] sm:h-[18rem]">
          <Link
            target='_blank'
            href={`https://www.amazon.com/s?k=${master.title}&tag=gearfocus07-20`}
          >
            <SafeImage
              title={`Buy ${master.title} New on ${affiliate}`}
              alt={`Buy ${master.title} New on ${affiliate}`}
              src={'https://gearfocus.b-cdn.net/logos/amazon-logo.svg'}
              className="object-contain"
              loading="lazy"
            />
          </Link>
        </div>
        <div className={`mt-[1.6rem] flex flex-col justify-start gap-[0.8rem]`}>
          <Link
            href={`https://www.amazon.com/s?k=${master.title}&tag=gearfocus07-20`}
            title={`View ${master.title} on Amazon`}
            target={'_blank'}
            className=" min-h-[4.8rem] text-[1.8rem] font-normal leading-[2.4rem]"
          >
            {master.title} on Amazon
          </Link>
          <div className="self-baseline">
            <p className="text-[1.8rem] font-bold leading-[2.4rem] text-brand-secondary">
              
            </p>
          </div>
        </div>
      </div>
    </div>
  )
});

AffiliateCard.displayName = 'AffiliateCard';

const CarouselComponent = ({
  items,
  affiliate,
  master,
  incrementBy,
  showPaginator = false,
}: CarouselProps) => {
  const INCREMENT_BY = incrementBy || 4;
  const FLEX_GAP = 16;
  const [currentIndex, setCurrent] = useState(0);
  const carouselfRef = useRef<HTMLDivElement>(null);
  const frontRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const frontEntry = useIntersectionObserver(frontRef, {});
  const endEntry = useIntersectionObserver(endRef, {});

  // Memoize handlers to prevent recreation on rerenders
  const handleLeftArrowClick = useCallback(() => {
    setCurrent((prev) => Math.max(prev - INCREMENT_BY, 0));
  }, [INCREMENT_BY]);
  
  const handleRightArrowClick = useCallback(() => 
    setCurrent((prev) => Math.min(prev + INCREMENT_BY, items.length)),
    [INCREMENT_BY, items.length]
  );

  // Optimize scroll effect to run only when needed
  useEffect(() => {
    if (!carouselfRef.current || !cardRef.current) return;
    
    const cardWidth = currentIndex * cardRef.current.offsetWidth;
    const cardSpacing = currentIndex * FLEX_GAP;
    const offset = cardWidth + cardSpacing;
    
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      if (carouselfRef.current) {
        carouselfRef.current.scrollLeft = offset;
      }
    });
  }, [currentIndex, FLEX_GAP]);

  // Memoize the rendering of affiliate items
  const renderAffiliateItems = useCallback((placement: 'start' | 'end') => {
    if (!master || !affiliate || affiliate.placement !== placement) return null;
    
    return affiliate.affiliates.map((affiliateItem, index) => (
      <AffiliateCard key={`affiliate-${placement}-${index}`} affiliate={affiliateItem} master={master} />
    ));
  }, [affiliate, master]);

  // Handle pagination click in a memoized callback
  const handlePaginationClick = useCallback((index: number) => {
    setCurrent(index);
  }, []);

  return (
    <div className="w-full min-w-[0rem] s:px-[4rem]">
      <div className="relative">
        {/* left arrow */}
        {!frontEntry?.isIntersecting && (
          <div className="absolute -left-8 top-0 z-20 hidden h-full items-center sm:flex">
            <Arrow variant="left" onClick={handleLeftArrowClick} />
          </div>
        )}

        <div
          className="overflow-y-hidden overflow-x-scroll scroll-smooth px-4 sm:px-0 lg:overflow-x-hidden"
          ref={carouselfRef}
        >
          <div className="flex w-min gap-[1.6rem]">
            {renderAffiliateItems('start')}

            {items.map((item, i) => {
              // append extra div to front of first item for frontRef
              if (i === 0) {
                return (
                  <div key={i} className="flex">
                    <div ref={frontRef} />
                    <div ref={i === 0 ? cardRef : null}>{item}</div>
                  </div>
                );
              }
              // append extra div to end of last item for endRef
              if (i === items.length - 1) {
                return (
                  <div key={i} className="flex">
                    <div ref={i === 0 ? cardRef : null}>{item}</div>
                    <div ref={endRef} />
                  </div>
                );
              }
              return <div key={i}>{item}</div>;
            })}

            {renderAffiliateItems('end')}
          </div>
        </div>

        {/* right arrow */}
        {!endEntry?.isIntersecting && (
          <div className="absolute -right-8 top-0 z-10 hidden h-full items-center sm:flex">
            <Arrow variant="right" onClick={handleRightArrowClick} />
          </div>
        )}

        {showPaginator && (
          <div className="mt-[1.6rem] flex justify-center gap-[0.8rem]">
            {items.map((_, i) => {
              const isCurrent = i === currentIndex;
              return (
                <div
                  key={i}
                  className={`h-[0.4rem] w-[2rem] rounded-full transition-colors sm:h-[0.8rem] sm:w-[4rem] ${
                    isCurrent ? 'bg-brand-secondary' : 'bg-gray-300'
                  }`}
                  onClick={() => handlePaginationClick(i)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Export a memoized version of the component to prevent unnecessary re-renders
export default memo(CarouselComponent);
